// src/pages/public/EditsPage.jsx
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Seo from "../../components/public/Seo";

import { listEdits, deleteEditMeta } from "../../state/editsStore";
import { getEditItems, deleteEditItems } from "../../state/editStore";
import { clearEditFeatures } from "../../state/editFeaturesStore";
import { getActiveEditId, setActiveEditId } from "../../state/activeEditStore";
import { optimizedImageUrl } from "../../lib/optimizedImageUrl";

function formatUpdated(ts) {
  if (!ts) return "";
  try {
    return new Date(ts).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

function ImageQuad({ images }) {
  const four = (images || []).slice(0, 4);

  return (
    <div
      style={{
        marginTop: 10,
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gap: 10,
      }}
    >
      {(four.length ? four : [null, null, null, null]).map((src, idx) => (
        <div
          key={`${src || "empty"}_${idx}`}
          style={{
            width: "100%",
            aspectRatio: "4 / 5",
            borderRadius: 14,
            overflow: "hidden",
            border: "1px solid rgba(0,0,0,0.08)",
            background: "rgba(0,0,0,0.05)",
          }}
        >
          {src ? (
           <img
  src={optimizedImageUrl(src, { width: 520, quality: 78 })}
  alt=""
  loading="lazy"
  decoding="async"
  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
/>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function NotesChips({ notes, expanded }) {
  if (!notes?.length) return null;

  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        flexWrap: "wrap",
        marginTop: 10,
        maxHeight: expanded ? "none" : 62, // ~2 rows
        overflow: expanded ? "visible" : "hidden",
      }}
    >
      {(expanded ? notes : notes.slice(0, 10)).map((t, idx) => (
        <span
          key={`note_${idx}`}
          className="public-muted"
          style={{
            fontSize: 11,
            padding: "4px 10px",
            borderRadius: 999,
            border: "1px solid rgba(0,0,0,0.10)",
            background: "rgba(0,0,0,0.03)",
            whiteSpace: "nowrap",
          }}
        >
          {t}
        </span>
      ))}
    </div>
  );
}

function ClickableHelper({ children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="public-muted"
      style={{
        marginTop: 8,
        fontSize: 12,
        padding: 0,
        border: 0,
        background: "transparent",
        cursor: "pointer",
        textDecoration: "underline",
        textUnderlineOffset: 3,
        color: "inherit",
      }}
    >
      {children}
    </button>
  );
}

export default function EditsPage() {
  const navigate = useNavigate();
  const [edits, setEdits] = useState([]);
  
  useEffect(() => {
    const metas = listEdits() || [];

    const view = metas.map((m) => {
      const items = getEditItems(m.id) || [];

      const images = items.filter((it) => it?.imageUrl).map((it) => it.imageUrl);

      // pull note texts from edit items
      const notes = items
        .filter((it) => String(it?.kind || "").toLowerCase() === "note")
        .map((it) => String(it?.text || "").trim())
        .filter(Boolean);

      return {
        id: m.id,
        name: (m.name || "").trim(),
        templateSlug: m.templateSlug || null,
        updatedAt: m.updatedAt || m.createdAt || null,
        pinCount: items.length,
        images,
        notes,
      };
    });

    setEdits(view);
  }, []);

  function openEdit(editId) {
    navigate(`/edits/${encodeURIComponent(editId)}`);
  }

  function doDelete(ev, e) {
    ev.preventDefault();
    ev.stopPropagation();

    const ok = window.confirm("Delete this edit? This cannot be undone.");
    if (!ok) return;

    const active = getActiveEditId();
    if (active === e.id) setActiveEditId("");

    deleteEditItems(e.id);
    clearEditFeatures(e.id);
    deleteEditMeta(e.id);

    setEdits((cur) => cur.filter((x) => x.id !== e.id));
  }

  return (
    <>
      <Seo title="Edits — Veya" description="Saved edits, pins, and looks." />

      <div className="public-breadcrumb">
        <Link to="/moods" className="public-navlink">
          Edits
        </Link>
        <span className="public-muted"> / Edits</span>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
          gap: 12,
        }}
      >
        {edits.map((e) => {
          
          return (
            <div
  key={e.id}
  className="public-card"
  onClick={() => openEdit(e.id)}
  style={{
    padding: 0,
    overflow: "hidden",
    textAlign: "left",
    position: "relative",
    cursor: "pointer",
  }}
>
              {/* Delete */}
              <button
  type="button"
  onClick={(ev) => doDelete(ev, e)}
  style={{
    position: "absolute",
    top: 8,
    right: 8,
    zIndex: 2,
    fontSize: 16,
    lineHeight: 1,
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    color: "rgba(0,0,0,0.6)",
  }}
  title="Delete"
>
  ×
</button>

              <div style={{ padding: 12 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 10, paddingRight: 76 }}>
                  <div style={{ fontWeight: 900, fontSize: 20, lineHeight: 1.15 }}>
                    {e.name || "Untitled edit"}
                  </div>

                  {e.templateSlug ? (
                    <span
                      className="public-muted"
                      style={{
                        fontSize: 11,
                        padding: "3px 8px",
                        borderRadius: 999,
                        border: "1px solid rgba(0,0,0,0.10)",
                        background: "rgba(0,0,0,0.03)",
                        whiteSpace: "nowrap",
                      }}
                      title="Created from a Veya template"
                    >
                      Template
                    </span>
                  ) : null}
                </div>

                <div className="public-muted" style={{ marginTop: 6, fontSize: 12 }}>
                  {e.pinCount} {e.pinCount === 1 ? "pin" : "pins"}
                  {e.updatedAt ? ` • Updated ${formatUpdated(e.updatedAt)}` : ""}
                </div>

                {/* 2x2 aesthetic grid */}
                <ImageQuad images={e.images} />

                {/* notes chips (2-row clamp) */}
                <NotesChips notes={e.notes} expanded={false} />

              </div>
            </div>
          );
        })}
      </div>

      {edits.length === 0 && (
        <p className="public-muted" style={{ marginTop: 14 }}>
          No saved edits yet.
        </p>
      )}
    </>
  );
}